import {
	Text,
	HStack,
	Button,
	VStack,
	H2,
	Tooltip,
} from '@montel/montelpro-shared-components-v2';
import { SUBSCRIPTIONS_API_ROUTE } from 'apiRoutes';
import { Company } from 'domain/company/Company';
import { FunctionComponent, useEffect, useState } from 'react';
import { useProviderBlacklistContext } from './ProviderBlacklistContext';
import { CompanySelect } from 'components/companies/CompanySelect';
import { useGet } from 'hooks/useGet';
import SectionDivider from 'components/common/SectionDivider';
import { ProviderBlacklistCompanyTable } from './ProviderBlacklistCompanyTable';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';

export const EditProviderBlacklist: FunctionComponent = () => {
	const [blacklistedCompanies, setBlacklistedCompanies] =
		useState<Company[]>();
	const [selectedCompany, setSelectedCompany] = useState<Company>();
	const [selectableCompanies, setSelectableCompanies] = useState<Company[]>();
	const [originalBlacklistedCompanies, setOriginalBlacklistedCompanies] =
		useState<Company[]>([]);
	const { adminIsRestrictedToRole } = useAuthRoles();

	const {
		allCompanies,
		selectedProvider,
		addedCompanies,
		removedCompanies,
		isLoading,
		setAddedCompanies,
		setRemovedCompanies,
		resetChanges,
		submitChanges,
	} = useProviderBlacklistContext();

	if (!selectedProvider) {
		return null;
	}

	const provider = selectedProvider;

	const { data: blacklistedCompaniesData, getData: getBlacklistedCompanies } =
		useGet<number[]>(
			`${SUBSCRIPTIONS_API_ROUTE}/GetProviderBlacklist?providerId=${provider.id}`,
		);

	useEffect(() => {
		if (allCompanies) {
			const selectable = allCompanies.filter(
				(c) =>
					!blacklistedCompanies?.some((bc) => bc.id === c.id) &&
					!addedCompanies.some((ac) => ac.id === c.id),
			);

			setSelectableCompanies(selectable);
		}
	}, [allCompanies, blacklistedCompanies, addedCompanies]);

	useEffect(() => {
		if (blacklistedCompaniesData) {
			const blacklistedCompanies = blacklistedCompaniesData
				.map((id) => allCompanies?.find((c) => c.id === id))
				.filter((c): c is Company => c !== undefined);

			setBlacklistedCompanies(blacklistedCompanies);
			setOriginalBlacklistedCompanies(blacklistedCompanies);
		}
	}, [blacklistedCompaniesData, allCompanies]);

	useEffect(() => {
		if (provider) {
			getBlacklistedCompanies();
			setOriginalBlacklistedCompanies([]);
		}
	}, [provider.id]);

	const addCompany = (company: Company) => {
		setAddedCompanies((prev) => [...prev, company]);
		setBlacklistedCompanies((prev) =>
			prev ? [...prev, company] : [company],
		);

		setRemovedCompanies((prev) => {
			if (prev.some((c) => c.id === company.id)) {
				return prev.filter((c) => c.id !== company.id);
			}
			return prev;
		});
	};

	const removeCompany = (company: Company) => {
		setBlacklistedCompanies((prev) =>
			prev ? prev.filter((c) => c.id !== company.id) : [],
		);

		const wasAdded = addedCompanies.some((c) => c.id === company.id);

		setAddedCompanies((prev) => {
			if (prev.some((c) => c.id === company.id)) {
				return prev.filter((c) => c.id !== company.id);
			}
			return prev;
		});

		if (!wasAdded) {
			setRemovedCompanies((prev) => [...prev, company]);
		}
	};

	const onCompanySelected = (companyId: number) => {
		const company = allCompanies?.find((c) => c.id === companyId);

		if (company) {
			addCompany(company);
			setSelectedCompany(undefined);
		}
	};

	const onResetChanges = () => {
		resetChanges();
		setBlacklistedCompanies(originalBlacklistedCompanies);
	};

	const onSubmit = async () => {
		await submitChanges();
		getBlacklistedCompanies();
	};

	const isDirty = addedCompanies.length > 0 || removedCompanies.length > 0;
	const disableEditBlacklist = adminIsRestrictedToRole(AdminRole.SALES);

	return (
		<VStack gap="lg" fullWidth>
			<SectionDivider />
			<HStack align="center" gap="xs">
				<H2>{provider.name} company blacklist </H2>
				{isDirty && (
					<Tooltip
						content="You have unsaved changes. Remember to save or undo your changes before navigating away from this page."
						component={
							<Text as="span" color="bgSuccess">
								(unsaved changes)
							</Text>
						}
					>
						<VStack gap="xs">
							{addedCompanies.length > 0 && (
								<Text>{addedCompanies.length} added</Text>
							)}
							{removedCompanies.length > 0 && (
								<Text>{removedCompanies.length} removed</Text>
							)}
						</VStack>
					</Tooltip>
				)}
			</HStack>
			<HStack gap="md" fullWidth>
				<Button
					text="Save changes"
					size="sm"
					type="submit"
					isLoading={isLoading}
					loadingText="Updating..."
					onClick={onSubmit}
					isDisabled={isLoading || !isDirty || disableEditBlacklist}
				/>
				<Button
					text="Undo changes"
					size="sm"
					variant="tertiary"
					onClick={onResetChanges}
					isDisabled={isLoading || !isDirty || disableEditBlacklist}
				/>
			</HStack>
			<VStack gap="lg" fullWidth>
				{blacklistedCompanies?.length ? (
					<ProviderBlacklistCompanyTable
						blacklistedCompanies={blacklistedCompanies}
						removeCompany={removeCompany}
						addedCompanies={addedCompanies}
						disabled={disableEditBlacklist}
					/>
				) : (
					<NoCompaniesSelectedText
						originalBlacklistedCompanies={
							originalBlacklistedCompanies
						}
					/>
				)}
				{allCompanies &&
					selectableCompanies &&
					!disableEditBlacklist && (
						<VStack gap="md" fullWidth>
							<Text>
								Add a company to the blacklist by selecting it
								from the dropdown below:
							</Text>
							<CompanySelect
								companies={selectableCompanies}
								value={
									selectedCompany ? selectedCompany.id : null
								}
								onChange={onCompanySelected}
								label=""
							/>
						</VStack>
					)}
			</VStack>
			<SectionDivider />
			<SectionDivider />
			<SectionDivider />
			<SectionDivider />
		</VStack>
	);
};

const NoCompaniesSelectedText: React.FC<{
	originalBlacklistedCompanies: Company[] | undefined;
}> = ({ originalBlacklistedCompanies }) => {
	if (
		originalBlacklistedCompanies &&
		originalBlacklistedCompanies.length > 0
	) {
		return (
			<Text fontStyle="italic" color="textError">
				All providers will be removed from the blacklist.
			</Text>
		);
	}

	return (
		<Text fontStyle="italic">Provider has no blacklisted companies.</Text>
	);
};
